import React, { useMemo } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import axios from '../api';
import { CUSTOM_AD_PROPERTIES } from '../custom_ad_properties';

const FilterSidebar = ({ filters, onChange, onSearch, loading }) => {
  // Helper to get custom fields for selected category
  const customFields = useMemo(() => {
    if (!filters.category) return [];
    const catId = Number(filters.category);
    return CUSTOM_AD_PROPERTIES[catId] || [];
  }, [filters.category]);
  // Handle custom field change
  const handleCustomFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = value;
    if (type === 'checkbox') val = checked;
    onChange({ target: { name, value: val }, filters: { ...filters, [name]: val } });
  };
  const [categories, setCategories] = React.useState([]);
  // Helper to convert Farsi digits and comma to English for API
  const faToEn = s => s.replace(/[۰-۹]/g, d => String.fromCharCode(d.charCodeAt(0) - 1728)).replace(/،/g, ',');

  const handleSearch = (e) => {
    e.preventDefault();
    // Prepare filter values for API
    const params = {};
    if (filters.category) params.category = filters.category;
    if (filters.category == 3) {
      if (filters.deposit_min) params.deposit_min = faToEn(filters.deposit_min).replace(/[^0-9]/g, '');
      if (filters.deposit_max) params.deposit_max = faToEn(filters.deposit_max).replace(/[^0-9]/g, '');
      if (filters.monthly_rent_min) params.monthly_rent_min = faToEn(filters.monthly_rent_min).replace(/[^0-9]/g, '');
      if (filters.monthly_rent_max) params.monthly_rent_max = faToEn(filters.monthly_rent_max).replace(/[^0-9]/g, '');
    } else {
      if (filters.price_min) params.price_min = faToEn(filters.price_min).replace(/[^0-9]/g, '');
      if (filters.price_max) params.price_max = faToEn(filters.price_max).replace(/[^0-9]/g, '');
    }
    if (filters.text) params.text = filters.text;
    if (filters.has_note) params.has_note = true;
    if (filters.saved_only) params.saved_only = true;
    // Add custom fields as custom_field_<name>
    customFields.forEach(field => {
      const key = `custom_field_${field.name}`;
      if (filters[key] !== undefined && filters[key] !== "") {
        params[key] = filters[key];
      }
    });
    if (onSearch) onSearch(params);
  };
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [subAnchorEl, setSubAnchorEl] = React.useState(null);
  const [subSubAnchorEl, setSubSubAnchorEl] = React.useState(null);
  const [selectedCat, setSelectedCat] = React.useState({ main: null, sub: null, subsub: null });

  React.useEffect(() => {
    axios.get('/categories/').then(res => setCategories(res.data));
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSubAnchorEl(null);
    setSubSubAnchorEl(null);
  };
  const handleMainCatClick = (event, cat) => {
    setSelectedCat({ main: cat, sub: null, subsub: null });
    setSubAnchorEl(event.currentTarget);
    setSubSubAnchorEl(null);
  };
  const handleSubCatClick = (event, sub) => {
    setSelectedCat(prev => ({ ...prev, sub, subsub: null }));
    setSubSubAnchorEl(event.currentTarget);
  };
  const handleCatSelect = (catId) => {
    onChange({ target: { name: 'category', value: catId } });
    handleMenuClose();
  };

  // Dynamically format price input with Farsi comma
  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    // Remove all non-digit chars, convert Farsi digits to English
    const enValue = value.replace(/[۰-۹]/g, d => String.fromCharCode(d.charCodeAt(0) - 1728)).replace(/[^0-9]/g, '');
    // Format with English comma, then convert to Farsi digits and comma
    const withComma = enValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const enToFa = s => s.replace(/[0-9]/g, d => String.fromCharCode(d.charCodeAt(0) + 1728));
    const faValue = enToFa(withComma).replace(/,/g, '،');

    // Prepare new filters object for dynamic logic
    let newFilters = { ...filters, [name]: faValue };

    // Convert Farsi price values to English numbers for comparison
    const faToEnNum = s => Number(s ? s.replace(/[۰-۹]/g, d => String.fromCharCode(d.charCodeAt(0) - 1728)).replace(/،/g, '').replace(/,/g, '') : 0);
    const minPrice = faToEnNum(name === 'price_min' ? value : filters.price_min);
    const maxPrice = faToEnNum(name === 'price_max' ? value : filters.price_max);

    // If both min and max are set, and max < min, update max
    if (minPrice && maxPrice && maxPrice < minPrice) {
      const newMax = Math.round(minPrice * 1.1);
      const newMaxStr = enToFa(newMax.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')).replace(/,/g, '،');
      newFilters.price_max = newMaxStr;
    }

    onChange({ target: { name, value: faValue }, filters: newFilters });
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 0, boxShadow: 'none', p: 0, direction: 'rtl', display: 'flex', flexDirection: 'column', gap: 2, minHeight: '100vh', justifyContent: 'flex-start', bgcolor: 'background.paper' }}>
      <Typography variant="h6" fontWeight={800} color="primary" align="right" gutterBottom>فیلترها</Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={handleMenuOpen} sx={{ textAlign: 'right', fontFamily: 'Vazirmatn', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ flex: 1 }}>
            {filters.category
              ? `دسته‌بندی انتخاب شده: ${getCategoryName(categories, filters.category)}`
              : 'انتخاب دسته‌بندی'}
          </span>
          {filters.category && (
            <IconButton aria-label="پاک کردن دسته‌بندی" size="small" onClick={e => {
              e.stopPropagation();
              onChange({ target: { name: 'category', value: null } });
              setSelectedCat({ main: null, sub: null, subsub: null });
              handleMenuClose();
            }} sx={{ ml: 1 }}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ style: { position: 'absolute', left: 0, top: 40, minWidth: 200, zIndex: 1300 } }}>
          {categories.map(cat => (
            <MenuItem
              key={cat.id}
              onClick={cat.children?.length ? (e) => handleMainCatClick(e, cat) : () => handleCatSelect(cat.id)}
              sx={{ justifyContent: 'space-between', fontFamily: 'Vazirmatn', bgcolor: filters.category == cat.id ? 'primary.light' : undefined, color: filters.category == cat.id ? 'primary.contrastText' : undefined, fontWeight: filters.category == cat.id ? 700 : undefined }}
            >
              {cat.children?.length ? <ArrowLeftIcon fontSize="small" style={{ marginLeft: 8 }} /> : null}
              {cat.name}
            </MenuItem>
          ))}
        </Menu>
        {selectedCat.main?.children?.length > 0 && (
          <Menu anchorEl={subAnchorEl} open={Boolean(subAnchorEl)} onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ style: { position: 'absolute', left: 210, top: 40, minWidth: 200, zIndex: 1300 } }}>
            {selectedCat.main.children.map(sub => (
              <MenuItem
                key={sub.id}
                onClick={sub.children?.length ? (e) => handleSubCatClick(e, sub) : () => handleCatSelect(sub.id)}
                sx={{ justifyContent: 'space-between', fontFamily: 'Vazirmatn', bgcolor: filters.category == sub.id ? 'primary.light' : undefined, color: filters.category == sub.id ? 'primary.contrastText' : undefined, fontWeight: filters.category == sub.id ? 700 : undefined }}
              >
                {sub.children?.length ? <ArrowLeftIcon fontSize="small" style={{ marginLeft: 8 }} /> : null}
                {sub.name}
              </MenuItem>
            ))}
          </Menu>
        )}
        {selectedCat.sub?.children?.length > 0 && (
          <Menu anchorEl={subSubAnchorEl} open={Boolean(subSubAnchorEl)} onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ style: { position: 'absolute', left: 420, top: 40, minWidth: 200, zIndex: 1300 } }}>
            {selectedCat.sub.children.map(subsub => (
              <MenuItem
                key={subsub.id}
                onClick={() => handleCatSelect(subsub.id)}
                sx={{ fontFamily: 'Vazirmatn', bgcolor: filters.category == subsub.id ? 'primary.light' : undefined, color: filters.category == subsub.id ? 'primary.contrastText' : undefined, fontWeight: filters.category == subsub.id ? 700 : undefined }}
              >
                {subsub.name}
              </MenuItem>
            ))}
          </Menu>
        )}
      </FormControl>
      {/* Apartment rental: show ودیعه and اجاره ماهانه min/max instead of price min/max */}
      {filters.category == 3 ? (
        <>
          <TextField name="deposit_min" value={filters.deposit_min || ''} onChange={handlePriceChange} label="حداقل ودیعه (تومان)" variant="outlined" size="small" sx={{ mb: 2 }} fullWidth inputProps={{ style: { textAlign: 'right' } }} />
          <TextField name="deposit_max" value={filters.deposit_max || ''} onChange={handlePriceChange} label="حداکثر ودیعه (تومان)" variant="outlined" size="small" sx={{ mb: 2 }} fullWidth inputProps={{ style: { textAlign: 'right' } }} />
          <TextField name="monthly_rent_min" value={filters.monthly_rent_min || ''} onChange={handlePriceChange} label="حداقل اجاره ماهانه (تومان)" variant="outlined" size="small" sx={{ mb: 2 }} fullWidth inputProps={{ style: { textAlign: 'right' } }} />
          <TextField name="monthly_rent_max" value={filters.monthly_rent_max || ''} onChange={handlePriceChange} label="حداکثر اجاره ماهانه (تومان)" variant="outlined" size="small" sx={{ mb: 2 }} fullWidth inputProps={{ style: { textAlign: 'right' } }} />
        </>
      ) : (
        <>
          <TextField name="price_min" value={filters.price_min} onChange={handlePriceChange} label="حداقل قیمت (تومان)" variant="outlined" size="small" sx={{ mb: 2 }} fullWidth inputProps={{ style: { textAlign: 'right' } }} />
          <TextField name="price_max" value={filters.price_max} onChange={handlePriceChange} label="حداکثر قیمت (تومان)" variant="outlined" size="small" sx={{ mb: 2 }} fullWidth inputProps={{ style: { textAlign: 'right' } }} />
        </>
      )}
      <TextField name="text" value={filters.text} onChange={onChange} label="عبارت جستجو" variant="outlined" size="small" sx={{ mb: 2 }} fullWidth inputProps={{ style: { textAlign: 'right' } }} />

      {/* Custom fields for selected category */}
      {customFields.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary" align="right" gutterBottom>
            فیلترهای اختصاصی دسته‌بندی
          </Typography>
          {customFields.map(field => {
            const key = `custom_field_${field.name}`;
            if (field.type === 'number' || field.type === 'text') {
              return (
                <TextField
                  key={key}
                  name={key}
                  value={filters[key] || ''}
                  onChange={handleCustomFieldChange}
                  label={field.name}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2, fontFamily: 'Vazirmatn' }}
                  fullWidth
                  inputProps={{ style: { textAlign: 'right', fontFamily: 'Vazirmatn' }, type: field.type === 'number' ? 'number' : 'text' }}
                />
              );
            } else if (field.type === 'select') {
              return (
                <FormControl key={key} fullWidth sx={{ mb: 2, fontFamily: 'Vazirmatn' }} size="small">
                  <TextField
                    select
                    name={key}
                    value={filters[key] || ''}
                    onChange={handleCustomFieldChange}
                    label={field.name}
                    variant="outlined"
                    size="small"
                    inputProps={{ style: { textAlign: 'right', fontFamily: 'Vazirmatn' } }}
                    sx={{ fontFamily: 'Vazirmatn' }}
                  >
                    <MenuItem value="" sx={{ fontFamily: 'Vazirmatn' }}>همه</MenuItem>
                    {field.options.map(opt => (
                      <MenuItem key={opt} value={opt} sx={{ fontFamily: 'Vazirmatn' }}>{opt}</MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              );
            } else if (field.type === 'switch') {
              return (
                <FormControl key={key} fullWidth sx={{ mb: 2, flexDirection: 'row', alignItems: 'center', fontFamily: 'Vazirmatn' }}>
                  <input
                    type="checkbox"
                    name={key}
                    checked={!!filters[key]}
                    onChange={handleCustomFieldChange}
                    style={{ marginLeft: 8 }}
                  />
                  <label htmlFor={key} style={{ fontFamily: 'Vazirmatn', fontWeight: 700, cursor: 'pointer', color: '#1976d2' }}>
                    {field.name}
                  </label>
                </FormControl>
              );
            }
            return null;
          })}
        </div>
      )}
      {/* آگهی های با یادداشت checkbox - moved above search button */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <input
            type="checkbox"
            id="has_note"
            name="has_note"
            checked={!!filters.has_note}
            onChange={e => onChange({ target: { name: 'has_note', value: e.target.checked } })}
            style={{ marginLeft: 8 }}
          />
          <label htmlFor="has_note" style={{ fontFamily: 'Vazirmatn', fontWeight: 700, cursor: 'pointer', color: '#1976d2' }}>
            آگهی های با یادداشت
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <input
            type="checkbox"
            id="saved_only"
            name="saved_only"
            checked={!!filters.saved_only}
            onChange={e => onChange({ target: { name: 'saved_only', value: e.target.checked } })}
            style={{ marginLeft: 8 }}
          />
          <label htmlFor="saved_only" style={{ fontFamily: 'Vazirmatn', fontWeight: 700, cursor: 'pointer', color: '#1976d2' }}>
            آگهی های نشان شده
          </label>
        </div>
      </FormControl>
      <button onClick={handleSearch} style={{ width: '100%', padding: '8px 0', fontWeight: 'bold', fontSize: '1rem', borderRadius: 4, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer' }} disabled={loading}>
        {loading ? 'در حال جستجو...' : 'جستجو'}
      </button>
    </Paper>
  );
};

function getCategoryName(categories, id) {
  for (const cat of categories) {
    if (cat.id === id) return cat.name;
    if (cat.children) {
      for (const sub of cat.children) {
        if (sub.id === id) return sub.name;
        if (sub.children) {
          for (const subsub of sub.children) {
            if (subsub.id === id) return subsub.name;
          }
        }
      }
    }
  }
  return '';
}
function formatPrice(value) {
  if (!value) return '';
  // Convert to English string, add comma, then convert digits to Farsi
  const enStr = value.toString();
  const withComma = enStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const enToFa = s => s.replace(/[0-9]/g, d => String.fromCharCode(d.charCodeAt(0) + 1728));
  // Replace English comma with Farsi comma (U+060C)
  return enToFa(withComma).replace(/,/g, '،');
}

export default FilterSidebar;
